import React, { useState } from 'react';
import {
  Stack,
  Title,
  Tabs,
  Group,
  ActionIcon,
  Text,
  SimpleGrid,
  UnstyledButton,
  Paper,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { PaymentHistoryList } from './PaymentHistoryList';
import { NavigationWrapper } from '../components/navigation/NavigationWrapper';
import { primaryColor } from '../constants/theme';
import { useApp } from '../context/AppContext';

const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const PaymentHistory: React.FC = () => {
  const [transactionType, setTransactionType] = useState<'Received' | 'Sent'>(
    'Received'
  );
  const [year, setYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('January');
  const { scrollAreaHeight, isTabletAndBelow } = useApp();

  return (
    <NavigationWrapper>
      <Stack align="center">
        <Paper
          shadow="sm"
          p="lg"
          radius="md"
          w="100%"
          maw={1200}
          data-testid="payment-history-container"
          mih={isTabletAndBelow ? scrollAreaHeight : 'auto'}
        >
          <Title order={3} fw="normal" mb="md">
            Payment History
          </Title>

          <Tabs
            value={transactionType}
            onChange={(v) => setTransactionType(v as 'Received' | 'Sent')}
            mb="md"
            color={primaryColor}
            data-testid="payment-history-tabs"
          >
            <Group gap={0}>
              <Tabs.Tab
                value="Received"
                color={primaryColor}
                w="50%"
                data-testid="payment-history-tab-received"
              >
                <Title order={4} fw="normal">
                  Received
                </Title>
              </Tabs.Tab>
              <Tabs.Tab
                value="Sent"
                color={primaryColor}
                w="50%"
                data-testid="payment-history-tab-sent"
              >
                <Title order={4} fw="normal">
                  Sent
                </Title>
              </Tabs.Tab>
            </Group>
          </Tabs>

          <Group justify="center" mb="md">
            <ActionIcon
              variant="subtle"
              color={primaryColor}
              onClick={() => setYear(year - 1)}
              data-testid="payment-history-year-prev"
              size="lg"
            >
              <IconChevronLeft size={24} />
            </ActionIcon>
            <Text
              fw={500}
              size="xl"
              w={80}
              ta="center"
              data-testid="payment-history-year"
            >
              {year}
            </Text>
            <ActionIcon
              variant="subtle"
              color={primaryColor}
              onClick={() => setYear(year + 1)}
              data-testid="payment-history-year-next"
              size="lg"
            >
              <IconChevronRight size={24} />
            </ActionIcon>
          </Group>

          <SimpleGrid cols={3} spacing="xs">
            {months.map((month) => {
              const isSelected = month === selectedMonth;
              return (
                <UnstyledButton
                  key={month}
                  onClick={() => setSelectedMonth(month)}
                  data-testid={`payment-history-month-${month.toLowerCase()}`}
                >
                  <Paper
                    p="xs"
                    radius="md"
                    ta="center"
                    style={{
                      backgroundColor: isSelected ? primaryColor : '#f8f9fa',
                      border: `1.5px solid ${isSelected ? primaryColor : 'transparent'}`,
                      color: isSelected ? '#fff' : 'inherit',
                      transition: 'all 150ms ease',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.borderColor = primaryColor;
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.borderColor = 'transparent';
                    }}
                  >
                    <Text size="sm" fw={isSelected ? 700 : 400}>
                      {month.slice(0, 3)}
                    </Text>
                  </Paper>
                </UnstyledButton>
              );
            })}
          </SimpleGrid>

          <PaymentHistoryList
            year={year.toString()}
            transactionType={transactionType}
            month={selectedMonth}
          />
        </Paper>
      </Stack>
    </NavigationWrapper>
  );
};

export default PaymentHistory;
